import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { addDays, differenceInDays, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Building2, Calendar, Crown, LogOut, Settings2, MonitorPlay } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { supabase } from '@/integrations/supabase/client'
import { useCompany } from '@/lib/CompanyProvider'

export const Route = createFileRoute('/master/tenants')({
  component: MasterTenants,
})

function MasterTenants() {
  const queryClient = useQueryClient()
  const { setActiveCompanyId } = useCompany()
  const navigate = useNavigate()

  const [selectedTenant, setSelectedTenant] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newPlanId, setNewPlanId] = useState<string>('')
  const [expirationDate, setExpirationDate] = useState<string>('')
  const [dateInputVal, setDateInputVal] = useState('')

  useEffect(() => {
    if (expirationDate) {
      const parts = expirationDate.split('-')
      if (parts.length === 3) {
        setDateInputVal(`${parts[2]}/${parts[1]}/${parts[0]}`)
      }
    } else {
      setDateInputVal('')
    }
  }, [expirationDate])

  // Fetch tenants (company_settings) and their subscriptions
  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['master-tenants'],
    queryFn: async () => {
      const [{ data: companies }, { data: subscriptions }, { data: plans }] = await Promise.all([
        supabase.from('company_settings').select('*'),
        supabase.from('company_subscriptions').select('*'),
        supabase.from('system_plans').select('id, name'),
      ])

      const debugCompany = companies?.find((c) => c.name === 'JATO TESTE')
      if (debugCompany) {
        console.log('REFETCH COMPANIES:', debugCompany)
        console.log(
          'REFETCH SUBSCRIPTIONS:',
          subscriptions?.filter((s) => s.company_id === debugCompany.id)
        )
      }

      if (!companies) return []

      return companies.map((company) => {
        const sub = subscriptions?.find((s) => s.company_id === company.id)

        // Nós sabemos que company_settings salva corretamente, então ele é a nossa fonte principal da verdade.
        // Se houver uma data na tabela settings, ela tem precedência.
        const effectiveSub = {
          ...(sub || {}),
          company_id: company.id,
          plan_id: company.plan_id || sub?.plan_id,
          status: company.subscription_status || sub?.status || 'ACTIVE',
          current_period_end: company.subscription_expires_at || sub?.current_period_end,
        }

        const plan = plans?.find((p) => p.id === (effectiveSub.plan_id || company.plan_id))
        return {
          ...company,
          subscription: effectiveSub,
          plan: plan || null,
        }
      })
    },
  })

  // Fetch plans for the dropdown
  const { data: plans = [] } = useQuery({
    queryKey: ['system-plans-dropdown'],
    queryFn: async () => {
      const { data } = await supabase.from('system_plans').select('id, name').eq('status', 'ACTIVE')
      return data || []
    },
  })

  const toggleFeature = useMutation({
    mutationFn: async ({ id, features }: { id: string, features: any }) => {
      const { error } = await supabase.from('company_settings').update({ custom_features: features }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['master-tenants'] })
      queryClient.invalidateQueries({ queryKey: ['active-tenant-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['company_settings'] })
      queryClient.invalidateQueries({ queryKey: ['user-companies'] })
      toast.success('Feature atualizada')
    },
    onError: (e: any) => toast.error(e.message),
  })

  const updateSubMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTenant || !newPlanId || !expirationDate) return

      const targetIso = new Date(`${expirationDate}T23:59:59.000Z`).toISOString()

      const subPayload: any = {
        plan_id: newPlanId,
        status: 'ACTIVE',
        current_period_end: targetIso,
      }

      // 1. Força a criação ou atualização na company_subscriptions de forma segura (sem upsert para evitar erro de constraint)
      const { data: existingSub, error: checkError } = await supabase
        .from('company_subscriptions')
        .select('company_id')
        .eq('company_id', selectedTenant.id)
        .maybeSingle()

      if (checkError) throw new Error(`Erro ao verificar assinatura: ${checkError.message}`)

      if (existingSub) {
        // Já existe, vamos atualizar
        const { data: updData, error: updError } = await supabase
          .from('company_subscriptions')
          .update(subPayload)
          .eq('company_id', selectedTenant.id)
          .select()

        if (updError) throw new Error(`Falha no update company_subscriptions: ${updError.message}`)
        if (!updData || updData.length === 0)
          throw new Error(`Supabase bloqueou o UPDATE em company_subscriptions (Verifique RLS)`)
      } else {
        // Não existe, vamos inserir
        const { data: insData, error: insError } = await supabase
          .from('company_subscriptions')
          .insert({ company_id: selectedTenant.id, ...subPayload })
          .select()

        if (insError) throw new Error(`Falha no insert company_subscriptions: ${insError.message}`)
        if (!insData || insData.length === 0)
          throw new Error(`Supabase bloqueou o INSERT em company_subscriptions (Verifique RLS)`)
      }

      // 2. Tenta atualizar também na company_settings
      const { data: setData, error: settingsError } = await supabase
        .from('company_settings')
        .update({
          plan_id: newPlanId,
          subscription_status: 'ACTIVE',
          subscription_expires_at: targetIso,
        })
        .eq('id', selectedTenant.id)
        .select()

      if (settingsError) throw new Error(`Falha no company_settings: ${settingsError.message}`)
      if (!setData || setData.length === 0) {
        throw new Error(
          `O Supabase bloqueou a edição na company_settings. O seu usuário SUPERADMIN precisa de uma política RLS de UPDATE na tabela company_settings.`
        )
      }

      console.log('DADOS SALVOS NA COMPANY_SETTINGS:', setData[0])
      console.log('DATA ESPERADA:', targetIso)

      return { targetIso }
    },
    onSuccess: async (res) => {
      if (res?.targetIso) {
        // Atualiza imediatamente o cache em memória para a UI refletir no mesmo milissegundo
        queryClient.setQueryData(['master-tenants'], (oldData: any[]) => {
          if (!oldData) return []
          return oldData.map((t) => {
            if (t.id === selectedTenant?.id) {
              const updatedPlan = plans.find((p) => p.id === newPlanId) || t.plan
              return {
                ...t,
                plan: updatedPlan,
                plan_id: newPlanId,
                subscription_status: 'ACTIVE',
                subscription_expires_at: res.targetIso,
                subscription: {
                  ...(t.subscription || {}),
                  company_id: t.id,
                  plan_id: newPlanId,
                  status: 'ACTIVE',
                  current_period_end: res.targetIso,
                },
              }
            }
            return t
          })
        })
      }

      await queryClient.invalidateQueries({ queryKey: ['master-tenants'] })
      await queryClient.invalidateQueries({ queryKey: ['active-tenant-subscription'] })
      await queryClient.invalidateQueries({ queryKey: ['company_settings'] })
      setIsModalOpen(false)
      setSelectedTenant(null)
    },
    onError: (err: any) => {
      console.error('Erro na mutação de assinatura:', err)
      alert(err.message)
    },
  })

  const handleImpersonate = (companyId: string) => {
    setActiveCompanyId(companyId)
    setTimeout(() => {
      navigate({ to: '/app' })
    }, 100)
  }

  const openModal = (tenant: any) => {
    setSelectedTenant(tenant)
    setNewPlanId(
      tenant.plan?.id || tenant.subscription?.plan_id || tenant.plan_id || plans[0]?.id || ''
    )
    const currentEnd = tenant.subscription?.current_period_end || tenant.subscription_expires_at
    const initialDate = currentEnd ? new Date(currentEnd) : new Date()
    setExpirationDate(format(initialDate, 'yyyy-MM-dd'))
    setIsModalOpen(true)
  }

  if (isLoading) return <div className="text-amber-500 animate-pulse">Carregando Inquilinos...</div>

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-black text-white tracking-tight flex items-center gap-3">
          <Building2 className="text-amber-500" size={32} />
          Gestão de Inquilinos
        </h1>
        <p className="text-slate-400">
          Controle as assinaturas e acesse o painel das empresas cadastradas.
        </p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-900/80 border-b border-slate-800 text-slate-400 text-xs uppercase tracking-wider font-semibold">
                <th className="px-6 py-4">Empresa</th>
                <th className="px-6 py-4">Plano Atual</th>
                <th className="px-6 py-4">Vencimento</th>
                <th className="px-6 py-4 text-right">Ações Globais</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {tenants.map((tenant) => {
                const sub = tenant.subscription
                const isExpired =
                  sub?.current_period_end && new Date(sub.current_period_end) < new Date()
                const daysLeft = sub?.current_period_end
                  ? differenceInDays(new Date(sub.current_period_end), new Date())
                  : 0

                return (
                  <tr key={tenant.id} className="hover:bg-slate-800/20 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-200">{tenant.name || 'Sem Nome'}</div>
                      <div className="text-xs text-slate-500 font-mono mt-1">{tenant.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      {tenant.plan ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                          <Crown size={12} />
                          {tenant.plan.name}
                        </span>
                      ) : (
                        <span className="text-slate-500 text-xs italic">Sem Assinatura</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {sub?.current_period_end ? (
                        <div className="flex flex-col gap-1">
                          <span
                            className={`text-sm font-medium ${isExpired ? 'text-red-400' : 'text-slate-300'}`}
                          >
                            {format(new Date(sub.current_period_end), "dd 'de' MMM, yyyy", {
                              locale: ptBR,
                            })}
                          </span>
                          <span
                            className={`text-xs ${isExpired ? 'text-red-500/70 font-bold' : 'text-slate-500'}`}
                          >
                            {isExpired ? 'Expirado' : `Restam ${daysLeft} dias`}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-500 text-xs">Indefinido</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            const currentFeatures = tenant.custom_features || {}
                            toggleFeature.mutate({
                              id: tenant.id,
                              features: { ...currentFeatures, publicidade_tela: !currentFeatures.publicidade_tela }
                            })
                          }}
                          className={`p-2 rounded-lg transition-colors ${tenant.custom_features?.publicidade_tela ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-400 hover:text-amber-500'}`}
                          title="Alternar Publicidade em Tela"
                        >
                          <MonitorPlay size={16} />
                        </button>
                        <button
                          onClick={() => openModal(tenant)}
                          className="p-2 bg-slate-800 hover:bg-amber-500 hover:text-slate-950 text-slate-400 rounded-lg transition-colors"
                          title="Gerenciar Assinatura"
                        >
                          <Settings2 size={16} />
                        </button>
                        <button
                          onClick={() => handleImpersonate(tenant.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded-lg transition-colors"
                        >
                          <LogOut size={16} className="rotate-180" />
                          Acessar Painel
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Subscription Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => !open && setIsModalOpen(false)}>
        <DialogContent className="sm:max-w-[450px] bg-slate-900 border-slate-800 text-slate-100 p-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-white">Editar Assinatura</h3>
              <p className="text-sm text-slate-400 mt-1">{selectedTenant?.name}</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Alterar Plano
                </label>
                <select
                  value={newPlanId}
                  onChange={(e) => setNewPlanId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:ring-1 focus:ring-amber-500 outline-none"
                >
                  <option value="" disabled>
                    Selecione um plano
                  </option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Vencimento (Data exata ou dias a adicionar/remover)
                </label>

                <div className="flex gap-3 items-center">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="dd/mm/aaaa"
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:ring-1 focus:ring-amber-500 outline-none placeholder-slate-600"
                      value={dateInputVal}
                      onChange={(e) => {
                        const raw = e.target.value
                        const digits = raw.replace(/\D/g, '').slice(0, 8)
                        let formatted = digits
                        if (digits.length > 2) {
                          formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`
                        }
                        if (digits.length > 4) {
                          formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
                        }
                        setDateInputVal(formatted)

                        if (formatted.length === 10) {
                          const parts = formatted.split('/')
                          const yyyymmdd = `${parts[2]}-${parts[1]}-${parts[0]}`
                          setExpirationDate(yyyymmdd)
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const curr = expirationDate
                        ? new Date(`${expirationDate}T12:00:00`)
                        : new Date()
                      setExpirationDate(format(addDays(curr, -15), 'yyyy-MM-dd'))
                    }}
                    className="px-3 py-2 bg-slate-800 hover:bg-red-500/20 text-slate-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    -15
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const curr = expirationDate
                        ? new Date(`${expirationDate}T12:00:00`)
                        : new Date()
                      setExpirationDate(format(addDays(curr, -1), 'yyyy-MM-dd'))
                    }}
                    className="px-3 py-2 bg-slate-800 hover:bg-red-500/20 text-slate-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    -1
                  </button>

                  <div className="flex-1 relative flex items-center justify-center bg-slate-950 border border-slate-800 rounded-lg px-3 text-center">
                    <span className="text-xs font-semibold text-slate-400">
                      {expirationDate
                        ? differenceInDays(new Date(`${expirationDate}T12:00:00`), new Date()) > 0
                          ? `+${differenceInDays(new Date(`${expirationDate}T12:00:00`), new Date())} dias`
                          : `${differenceInDays(new Date(`${expirationDate}T12:00:00`), new Date())} dias`
                        : '0 dias'}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const curr = expirationDate
                        ? new Date(`${expirationDate}T12:00:00`)
                        : new Date()
                      setExpirationDate(format(addDays(curr, 1), 'yyyy-MM-dd'))
                    }}
                    className="px-3 py-2 bg-slate-800 hover:bg-emerald-500/20 text-slate-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    +1
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const curr = expirationDate
                        ? new Date(`${expirationDate}T12:00:00`)
                        : new Date()
                      setExpirationDate(format(addDays(curr, 30), 'yyyy-MM-dd'))
                    }}
                    className="px-3 py-2 bg-slate-800 hover:bg-emerald-500/20 text-slate-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    +30
                  </button>
                </div>

                {expirationDate && (
                  <p className="text-xs text-slate-500 text-center mt-3 flex items-center justify-center gap-1.5 bg-slate-950 py-2 rounded-lg border border-slate-800/50">
                    <Calendar size={14} className="text-amber-500" />
                    Nova data de expiração:{' '}
                    <strong className="text-amber-400 ml-1">
                      {format(new Date(`${expirationDate}T12:00:00`), "dd 'de' MMMM, yyyy", {
                        locale: ptBR,
                      })}
                    </strong>
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-slate-800">
              <Button
                variant="ghost"
                onClick={() => setIsModalOpen(false)}
                className="flex-1 hover:bg-slate-800 text-slate-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => updateSubMutation.mutate()}
                disabled={updateSubMutation.isPending}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"
              >
                {updateSubMutation.isPending ? 'Salvando...' : 'Salvar Assinatura'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
